import { zodResolver } from '@hookform/resolvers/zod';
import {
  authControllerChangePassword,
  authControllerMfaDisable,
  authControllerMfaSetup,
  authControllerMfaStatus,
  authControllerMfaVerifySetup,
  usersControllerFindOne,
  usersControllerUpdateOwnProfile,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  AlertCircle,
  Camera,
  CheckCircle2,
  Edit2,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  User,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormField } from '../../components/forms/form-field';
import { FormSection } from '../../components/forms/form-section';
import { AppLayout } from '../../components/layout/app-layout';
import { PageHeader } from '../../components/layout/page-header';
import { GradientAvatar } from '../../components/shared/gradient-avatar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PhoneInput } from '../../components/ui/phone-input';
import { UAE_PHONE_REGEX, toCanonicalUaePhone } from '../../lib/phone';
import { useAuthStore } from '../../stores/auth.store';

export const Route = createFileRoute('/settings/profile')({
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
  },
  component: ProfilePage,
});

// ── Zod schemas ────────────────────────────────────────────────────────────────

const profileSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  username: z.string().optional(),
  phoneNumber: z
    .string()
    .optional()
    .refine((v) => !v || UAE_PHONE_REGEX.test(v), {
      message: 'Enter a valid UAE mobile number',
    }),
});

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'At least 8 characters')
      .regex(/[A-Z]/, '1 uppercase letter required')
      .regex(/[0-9]/, '1 number required')
      .regex(/[^A-Za-z0-9]/, '1 special character required'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

const mfaCodeSchema = z.object({
  code: z.string().length(6, 'Must be exactly 6 digits'),
});

type ProfileValues = z.infer<typeof profileSchema>;
type PasswordValues = z.infer<typeof passwordSchema>;
type MfaCodeValues = z.infer<typeof mfaCodeSchema>;

// ── Shared helpers ─────────────────────────────────────────────────────────────

function AlertBanner({ type, message }: { type: 'success' | 'error'; message: string }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm mb-4 border ${
        type === 'success'
          ? 'bg-success-50 border-success-200 text-success-700'
          : 'bg-error-50 border-error-200 text-error-600'
      }`}
    >
      {type === 'success' ? (
        <CheckCircle2 className="w-4 h-4 shrink-0" />
      ) : (
        <AlertCircle className="w-4 h-4 shrink-0" />
      )}
      {message}
    </div>
  );
}

function PasswordInput({
  id,
  label,
  registration,
  show,
  onToggle,
  hint,
  error,
}: {
  id: string;
  label: string;
  registration: React.InputHTMLAttributes<HTMLInputElement> & { ref?: React.Ref<HTMLInputElement> };
  show: boolean;
  onToggle: () => void;
  hint?: string;
  error?: string;
}) {
  return (
    <FormField label={label} hint={hint} error={error}>
      <div className="relative">
        <Input id={id} type={show ? 'text' : 'password'} hasError={!!error} {...registration} />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </FormField>
  );
}

// ── ProfileSection ─────────────────────────────────────────────────────────────

function ProfileSection() {
  const { user, setSession, permissions, licensingAuthorities, token } = useAuthStore();
  const [editing, setEditing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: detail } = useQuery({
    queryKey: ['user', user?.id],
    queryFn: () =>
      usersControllerFindOne({ id: user!.id }) as unknown as Promise<{
        username: string | null;
        phoneNumber: string | null;
      }>,
    enabled: !!user,
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: user?.name ?? '', username: '', phoneNumber: '' },
  });

  // Pre-fill username/phoneNumber once the detail fetch resolves (not present on the auth store).
  useEffect(() => {
    if (detail) {
      reset({
        name: user?.name ?? '',
        username: detail.username ?? '',
        phoneNumber: toCanonicalUaePhone(detail.phoneNumber),
      });
    }
  }, [detail]);

  const mutation = useMutation({
    mutationFn: (data: ProfileValues) =>
      usersControllerUpdateOwnProfile({
        requestBody: {
          name: data.name,
          username: data.username || undefined,
          phoneNumber: data.phoneNumber || undefined,
        },
      }),
    onSuccess: (_res, data) => {
      setErrorMessage(null);
      setSession(
        { id: user!.id, email: user!.email, name: data.name },
        permissions,
        token!,
        false,
        licensingAuthorities,
      );
      setEditing(false);
    },
    onError: (err: unknown) => {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const raw = body?.message;
      const lines = Array.isArray(raw) ? raw : [raw ?? 'Failed to update profile.'];
      setErrorMessage(lines.join(' · '));
    },
  });

  return (
    <FormSection
      icon={<User className="w-4 h-4" />}
      iconBg="bg-brand-50 text-brand-600"
      title="Personal Information"
      subtitle="Your name, username, phone number, and email address"
      badge={
        !editing ? (
          <button
            onClick={() => {
              mutation.reset();
              setErrorMessage(null);
              setEditing(true);
            }}
            className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-900 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition-all"
          >
            <Edit2 className="w-3 h-3" /> Edit
          </button>
        ) : undefined
      }
    >
      {mutation.isSuccess && <AlertBanner type="success" message="Profile updated." />}
      {errorMessage && <AlertBanner type="error" message={errorMessage} />}

      {!editing ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {[
            { label: 'Full name', value: user?.name },
            { label: 'Username', value: detail?.username },
            { label: 'Phone number', value: detail?.phoneNumber },
            { label: 'Email address', value: user?.email },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
                {label}
              </p>
              <p className="text-sm font-semibold text-gray-900">{value ?? '—'}</p>
            </div>
          ))}
        </div>
      ) : (
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Full name" error={errors.name?.message}>
              <Input id="profile-name" hasError={!!errors.name} {...register('name')} />
            </FormField>
            <FormField label="Username" error={errors.username?.message}>
              <Input id="profile-username" hasError={!!errors.username} {...register('username')} />
            </FormField>
            <FormField label="Phone number" error={errors.phoneNumber?.message}>
              <Controller
                control={control}
                name="phoneNumber"
                render={({ field }) => (
                  <PhoneInput
                    id="profile-phone"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder="502305316"
                    hasError={!!errors.phoneNumber}
                  />
                )}
              />
            </FormField>
            <FormField
              label="Email address"
              hint="Only super administrators can change email, via the user management screen"
            >
              <Input value={user?.email} disabled className="opacity-60 cursor-not-allowed" />
            </FormField>
          </div>
          <div className="flex gap-2">
            <Button variant="success" size="sm" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => {
                reset({
                  name: user?.name ?? '',
                  username: detail?.username ?? '',
                  phoneNumber: toCanonicalUaePhone(detail?.phoneNumber),
                });
                mutation.reset();
                setErrorMessage(null);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </FormSection>
  );
}

// ── PasswordSection ────────────────────────────────────────────────────────────

function PasswordSection() {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const mutation = useMutation({
    mutationFn: (data: PasswordValues) =>
      authControllerChangePassword({
        requestBody: { currentPassword: data.currentPassword, newPassword: data.newPassword },
      }),
    onSuccess: () => reset(),
  });

  return (
    <FormSection
      icon={<Lock className="w-4 h-4" />}
      iconBg="bg-amber-50 text-amber-600"
      title="Change Password"
      subtitle="Choose a strong password you don't use elsewhere"
    >
      {mutation.isSuccess && (
        <AlertBanner type="success" message="Password changed successfully." />
      )}
      {mutation.isError && (
        <AlertBanner
          type="error"
          message={
            (mutation.error as { message?: string })?.message ?? 'Failed to change password.'
          }
        />
      )}

      <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
        <PasswordInput
          id="cur-pwd"
          label="Current password"
          registration={register('currentPassword')}
          show={showCurrent}
          onToggle={() => setShowCurrent((v) => !v)}
          error={errors.currentPassword?.message}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <PasswordInput
            id="new-pwd"
            label="New password"
            registration={register('newPassword')}
            show={showNew}
            onToggle={() => setShowNew((v) => !v)}
            hint="Min 8 chars, 1 uppercase, 1 number, 1 symbol"
            error={errors.newPassword?.message}
          />
          <PasswordInput
            id="confirm-pwd"
            label="Confirm new password"
            registration={register('confirmPassword')}
            show={false}
            onToggle={() => {}}
            error={errors.confirmPassword?.message}
          />
        </div>
        <Button variant="success" size="sm" type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </FormSection>
  );
}

// ── MfaSection ─────────────────────────────────────────────────────────────────

function MfaSection() {
  const qc = useQueryClient();
  const [step, setStep] = useState<'idle' | 'setup' | 'disable'>('idle');
  const [qrData, setQrData] = useState<{ qrCodeDataUrl: string; secret: string } | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaSuccess, setMfaSuccess] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset: resetCode,
    watch,
    formState: { errors: codeErrors },
  } = useForm<MfaCodeValues>({
    resolver: zodResolver(mfaCodeSchema),
    defaultValues: { code: '' },
  });

  const code = watch('code');

  const { data: status, isLoading } = useQuery({
    queryKey: ['mfa-status'],
    queryFn: () => authControllerMfaStatus() as unknown as Promise<{ mfaEnabled: boolean }>,
  });

  const setupMutation = useMutation({
    mutationFn: () =>
      authControllerMfaSetup() as unknown as Promise<{ qrCodeDataUrl: string; secret: string }>,
    onSuccess: (data) => {
      setQrData(data);
      setStep('setup');
      setMfaError(null);
    },
    onError: () => setMfaError('Failed to start MFA setup.'),
  });

  const verifyMutation = useMutation({
    mutationFn: (data: MfaCodeValues) =>
      authControllerMfaVerifySetup({ requestBody: { code: data.code } }) as unknown as Promise<{
        message: string;
      }>,
    onSuccess: () => {
      setMfaSuccess('MFA enabled successfully.');
      setStep('idle');
      setQrData(null);
      resetCode();
      qc.invalidateQueries({ queryKey: ['mfa-status'] });
    },
    onError: () => setMfaError('Invalid code. Try again.'),
  });

  const disableMutation = useMutation({
    mutationFn: (data: MfaCodeValues) =>
      authControllerMfaDisable({ requestBody: { code: data.code } }) as unknown as Promise<{
        message: string;
      }>,
    onSuccess: () => {
      setMfaSuccess('MFA disabled.');
      setStep('idle');
      resetCode();
      qc.invalidateQueries({ queryKey: ['mfa-status'] });
    },
    onError: () => setMfaError('Invalid code. Try again.'),
  });

  const enabled = status?.mfaEnabled;

  const badge = !isLoading && (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
        enabled
          ? 'bg-success-50 text-success-700 border-success-200'
          : 'bg-gray-100 text-gray-500 border-gray-200'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${enabled ? 'bg-success-500' : 'bg-gray-400'}`} />
      {enabled ? 'Enabled' : 'Disabled'}
    </span>
  );

  return (
    <FormSection
      icon={<Smartphone className="w-4 h-4" />}
      iconBg="bg-violet-50 text-violet-600"
      title="Two-Factor Authentication"
      subtitle="Add an extra layer of security with an authenticator app"
      badge={badge || undefined}
    >
      {mfaSuccess && <AlertBanner type="success" message={mfaSuccess} />}
      {mfaError && <AlertBanner type="error" message={mfaError} />}

      {isLoading && <div className="h-10 animate-pulse rounded-xl bg-gray-100" />}

      {!isLoading && step === 'idle' && (
        <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-4 py-3.5">
          <div className="flex items-center gap-3">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center ${enabled ? 'bg-success-100' : 'bg-gray-200'}`}
            >
              <KeyRound className={`w-4 h-4 ${enabled ? 'text-success-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {enabled ? 'MFA is active' : 'MFA is not configured'}
              </p>
              <p className="text-xs text-gray-500">
                {enabled
                  ? 'Your account is protected with an authenticator app.'
                  : 'Use Google Authenticator or Authy.'}
              </p>
            </div>
          </div>
          {!enabled ? (
            <Button
              variant="success"
              size="sm"
              onClick={() => {
                setMfaSuccess(null);
                setMfaError(null);
                setupMutation.mutate();
              }}
              disabled={setupMutation.isPending}
            >
              Enable MFA
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setMfaSuccess(null);
                setMfaError(null);
                setStep('disable');
              }}
            >
              Disable MFA
            </Button>
          )}
        </div>
      )}

      {step === 'setup' && qrData && (
        <div className="rounded-xl border border-gray-100 bg-gray-50 p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Scan the QR code with your authenticator app, then enter the 6-digit code to confirm.
          </p>
          <div className="flex items-start gap-6 flex-wrap">
            <div className="flex flex-col items-center gap-2">
              <div className="rounded-2xl border-2 border-gray-200 bg-white p-3 shadow-sm">
                <img src={qrData.qrCodeDataUrl} alt="MFA QR code" className="h-36 w-36" />
              </div>
              <details className="text-xs text-gray-400 text-center max-w-[160px]">
                <summary className="cursor-pointer hover:text-gray-600 transition-colors">
                  Can't scan? Manual code
                </summary>
                <code className="mt-2 block break-all rounded-lg bg-white border border-gray-200 p-2 font-mono text-left">
                  {qrData.secret}
                </code>
              </details>
            </div>
            <form
              onSubmit={handleSubmit((d) => verifyMutation.mutate(d))}
              className="flex-1 min-w-[180px] space-y-3"
            >
              <FormField label="6-digit verification code" error={codeErrors.code?.message}>
                <Input
                  id="mfa-verify"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  className="text-center tracking-[0.5em] text-lg font-mono max-w-[160px]"
                  hasError={!!codeErrors.code}
                  {...register('code', {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/\D/g, '');
                    },
                  })}
                />
              </FormField>
              <div className="flex gap-2">
                <Button
                  variant="success"
                  size="sm"
                  type="submit"
                  disabled={code.length !== 6 || verifyMutation.isPending}
                >
                  {verifyMutation.isPending ? 'Verifying…' : 'Activate MFA'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => {
                    setStep('idle');
                    setQrData(null);
                    resetCode();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {step === 'disable' && (
        <form
          onSubmit={handleSubmit((d) => disableMutation.mutate(d))}
          className="rounded-xl border border-error-100 bg-error-50 p-5 space-y-4"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-error-500 shrink-0" />
            <p className="text-sm text-error-700 font-medium">
              You are about to disable MFA protection.
            </p>
          </div>
          <p className="text-sm text-error-600">
            Enter your current authenticator code to confirm.
          </p>
          <FormField label="Authenticator code" error={codeErrors.code?.message}>
            <Input
              id="mfa-disable"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              className="text-center tracking-[0.5em] text-lg font-mono max-w-[160px]"
              hasError={!!codeErrors.code}
              {...register('code', {
                onChange: (e) => {
                  e.target.value = e.target.value.replace(/\D/g, '');
                },
              })}
            />
          </FormField>
          <div className="flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              type="submit"
              disabled={code.length !== 6 || disableMutation.isPending}
            >
              {disableMutation.isPending ? 'Disabling…' : 'Disable MFA'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => {
                setStep('idle');
                resetCode();
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </FormSection>
  );
}

// ── Profile sidebar card ───────────────────────────────────────────────────────

function ProfileCard() {
  const { user, permissions, setSession, token, licensingAuthorities } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { data: mfaStatus } = useQuery({
    queryKey: ['mfa-status'],
    queryFn: () => authControllerMfaStatus() as unknown as Promise<{ mfaEnabled: boolean }>,
  });

  const avatarMutation = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('/api/v1/users/me/avatar', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message ?? 'Upload failed');
      }
      return res.json() as Promise<{ avatarUrl: string }>;
    },
    onSuccess: (data) => {
      setUploadError(null);
      setSession(
        { id: user!.id, email: user!.email, name: user!.name, avatarUrl: data.avatarUrl },
        permissions,
        token!,
        false,
        licensingAuthorities,
      );
    },
    onError: (err: Error) => setUploadError(err.message),
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    avatarMutation.mutate(file);
    e.target.value = '';
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden lg:sticky lg:top-6">
      <div className="h-1.5 bg-gradient-to-r from-brand-400 via-indigo-500 to-violet-400" />
      <div className="flex flex-col items-center gap-3 px-6 py-7 text-center">
        <div className="relative group">
          <GradientAvatar
            name={user?.name ?? 'U'}
            avatarUrl={user?.avatarUrl ?? '/images/arabian.png'}
            size="lg"
            shape="rounded"
            className="h-20 w-20 text-2xl"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={avatarMutation.isPending}
            className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
            aria-label="Change profile picture"
          >
            {avatarMutation.isPending ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Camera className="h-5 w-5 text-white" />
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
        {uploadError && <p className="text-xs text-error-600">{uploadError}</p>}
        <div className="min-w-0 w-full">
          <h2 className="text-base font-bold text-gray-900 truncate">{user?.name}</h2>
          <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
        </div>
      </div>

      <div className="border-t border-gray-100 px-5 py-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Permissions</span>
          <span className="text-xs font-semibold text-gray-700 bg-gray-100 rounded-full px-2 py-0.5">
            {permissions.length} active
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Two-factor auth</span>
          {mfaStatus?.mfaEnabled ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-success-700">
              <ShieldCheck className="w-3.5 h-3.5" /> Enabled
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400">
              <ShieldOff className="w-3.5 h-3.5" /> Disabled
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

function ProfilePage() {
  return (
    <AppLayout>
      <PageHeader
        title="My Profile"
        description="Manage your account details and security settings"
      />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 items-start">
        <ProfileCard />

        <div className="space-y-4">
          <ProfileSection />
          <PasswordSection />
          <MfaSection />
        </div>
      </div>
    </AppLayout>
  );
}
